import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { getGaAttribution } from '../utils/ga-attribution.util';

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role_id: number;
  customers_id: string;
  role: { id: number; name: string };
  ownStore: string;
  token: string;
  token_type: string;
  expires_in: number;
  // Optional profile fields returned by some API responses
  firstname?: string;
  first_name?: string;
  lastname?: string;
  last_name?: string;
  mobile?: string;
  contact?: string;
  phone?: string;
}

export interface City {
  id: number;
  name: string;
  city?: string;
  state?: { name: string; country?: { name: string } };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  /**
   * OLD backend. Still the base for everything on this service that has not
   * moved: resetPassword, city_new, and the unused email/password login.
   */
  private BASE = `${environment.apiUrl}/api/unauth`;

  /**
   * NEW backend — the site's sign-in lives here now.
   *
   * The site is OTP-only (the email/password and forgot-password blocks are
   * commented out in auth-modal.component.html), so what moved is exactly three
   * calls: send a code, log in with one, register with one.
   *
   * The token that comes back is still a JWT, and deliberately so: this site
   * calls thirteen authenticated endpoints on the OLD backend, and
   * /api/auth/generateOrder is how an order is placed. The new backend mints a
   * token both backends accept, so those keep working while they migrate. That
   * is also why nothing else on this service changes — `auth_token` is still the
   * one credential, still sent to both hosts.
   */
  private SITE_AUTH = `${environment.newApiUrl}/api/site/auth`;
  private currentUser$ = new BehaviorSubject<AuthUser | null>(this.loadUser());
  private loginRequired$ = new BehaviorSubject<boolean>(false);

  user$ = this.currentUser$.asObservable();
  showLoginDialog$ = this.loginRequired$.asObservable();

  requestLogin() {
    this.loginRequired$.next(true);
  }

  loginDialogClosed() {
    this.loginRequired$.next(false);
  }

  constructor(private http: HttpClient) {}

  get currentUser(): AuthUser | null {
    return this.currentUser$.value;
  }

  get isLoggedIn(): boolean {
    return !!this.currentUser;
  }

  get token(): string | null {
    return localStorage.getItem('auth_token');
  }

  // --- API Methods ---

  // MIGRATED to the new backend. Field names are unchanged on purpose — the
  // payloads and the `{ data: { ..., token } }` response are the same contract,
  // so nothing downstream of these three calls needed touching.

  /** Was: POST {BASE}/register */
  register(data: { name: string; phone: string; otp: string; session_id?: string }): Observable<any> {
    return this.http.post(`${this.SITE_AUTH}/register`, { ...data, ...getGaAttribution() });
  }

  /** Was: POST {BASE}/generateOtp */
  generateOtp(data: { mobile_no: string; type: 'register' | 'login' }): Observable<any> {
    return this.http.post(`${this.SITE_AUTH}/otp`, data);
  }

  /** Was: POST {BASE}/loginWithOtp */
  loginWithOtp(data: { phone: string; otp: string; session_id?: string }): Observable<any> {
    return this.http.post(`${this.SITE_AUTH}/login`, data);
  }

  /**
   * Email + password. NOT migrated, because the site does not use it: the email,
   * password and "Forgot Password" blocks are all commented out in
   * auth-modal.component.html, so nothing can reach this. Left pointing at the
   * old backend rather than deleted — the form controls still exist in the
   * component, and a dead method is easier to explain than a missing one if
   * those inputs are ever uncommented.
   */
  login(data: { email: string; password: string; session_id?: string }): Observable<any> {
    return this.http.post(`${this.BASE}/login`, data);
  }

  resetPassword(email: string): Observable<any> {
    return this.http.get(`${this.BASE}/resetPassword`, { params: { email } });
  }

  getCities(pageSize = -1): Observable<any> {
    return this.http.get(`${this.BASE}/city_new`, { params: { pageSize: pageSize.toString() } });
  }

  // --- Token & User Management ---

  saveAuth(userData: AuthUser) {
    localStorage.setItem('auth_token', userData.token);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    this.currentUser$.next(userData);
  }

  logout() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    this.currentUser$.next(null);
  }

  private loadUser(): AuthUser | null {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }
}
